import { createSlice } from "@reduxjs/toolkit";

export const initialReducerState = {
    stripeCheckoutSessionClientSecret: null,
    addPaymentMethodLoading: false,
    addPaymentMethodErrorMessage: null,

    billingStatus: null,
    billingInformation: null,
    addBillingInformationErrorMessage: null,
    getBillingInformationErrorMessage: null,
    removeBillingInformationErrorMessage: null,

    billingAgreement: null,
    billingAgreementLoading: false,
    billingAgreementErrorMessage: null,
    billingAgreementUpdateLoading: false,

    updateBillingObligationLoading: false,
    updateBillingObligationErrorMessage: null,

    cancelBillingObligationLoading: false,
    cancelBillingObligationErrorMessage: null,

    chargePreview: null,
    chargePreviewLoading: false,
    chargePreviewErrorMessage: null,

    getBillingInformationLoading: false,
    addBillingInformationLoading: false,
    removeBillingInformationLoading: false,

    stripeDocuments: null,
    stripeDocumentsLoading: false,
    stripeDocumentsErrorMessage: null,

    appstoreTransactions: null,
    appstoreTransactionsLoading: false,
    appstoreTransactionsErrorMessage: null,
    appstoreTransactionsWarningMessage: null,
    showAllInvoices: false,
    shopifyStoreId: null,

    updateInvoiceItemLoading: {},
    chargeInvoiceItemLoading: {},
    getInvoiceItemsLoading: false,
    invoiceItems: null,
    invoiceItemsErrorMessage: null,
};

const organizationBillingSlice = createSlice({
    name: "OrganizationBilling",
    initialState: { ...initialReducerState },
    reducers: {
        getBillingStatusRequest(state) {
            state.isLoading = true;
            state.errorMessage = null;
        },
        getBillingStatusSuccess(state, action) {
            state.isLoading = false;
            state.billingStatus = action.payload;
        },
        getBillingStatusFailure(state, action) {
            state.isLoading = false;
            state.errorMessage = action.payload;
        },
        getChargePreviewRequest(state) {
            state.chargePreviewLoading = true;
            state.chargePreviewErrorMessage = undefined;
            state.chargePreview = null;
        },
        getChargePreviewSuccess(state, action) {
            state.chargePreviewLoading = false;
            state.chargePreview = action.payload;
        },
        getChargePreviewFailure(state, action) {
            state.chargePreviewLoading = false;
            state.chargePreviewErrorMessage = action.payload;
        },

        // BILLING AGREEMENT
        getBillingAgreementRequest(state) {
            state.billingAgreementLoading = true;
            state.billingAgreementErrorMessage = null;
            state.billingAgreement = null;
        },
        getBillingAgreementSuccess(state, action) {
            state.billingAgreementLoading = false;
            state.billingAgreementErrorMessage = null;
            state.billingAgreement = action.payload;
        },
        getBillingAgreementFailure(state, action) {
            state.billingAgreementLoading = false;
            state.billingAgreementErrorMessage = action.payload;
        },

        updateBillingAgreementRequest(state) {
            state.billingAgreementUpdateLoading = true;
            state.billingAgreementErrorMessage = null;
            state.billingAgreement = null;
        },
        updateBillingAgreementSuccess(state, action) {
            state.billingAgreementUpdateLoading = false;
            state.billingAgreementErrorMessage = null;
            state.billingAgreement = action.payload;
        },
        updateBillingAgreementFailure(state, action) {
            state.billingAgreementUpdateLoading = false;
            state.billingAgreementErrorMessage = action.payload;
        },

        // BILLING INFORMATION
        addBillingInformationRequest(state) {
            state.addBillingInformationLoading = true;
            state.addBillingInformationErrorMessage = null;
        },
        addBillingInformationSuccess(state, action) {
            state.addBillingInformationLoading = false;
            state.billingInformation = action.payload;
        },
        addBillingInformationFailure(state, action) {
            state.addBillingInformationLoading = false;
            state.addBillingInformationErrorMessage = action.payload;
        },

        getBillingInformationRequest(state) {
            state.getBillingInformationLoading = true;
            state.getBillingInformationErrorMessage = null;
        },
        getBillingInformationSuccess(state, action) {
            state.getBillingInformationLoading = false;
            state.billingInformation = action.payload;
        },
        getBillingInformationFailure(state, action) {
            state.getBillingInformationLoading = false;
            state.getBillingInformationErrorMessage = action.payload;
        },

        removeBillingInformationRequest(state) {
            state.removeBillingInformationLoading = true;
            state.removeBillingInformationErrorMessage = null;
        },
        removeBillingInformationSuccess(state, action) {
            state.removeBillingInformationLoading = false;
            state.billingInformation = action.payload;
        },
        removeBillingInformationFailure(state, action) {
            state.removeBillingInformationLoading = false;
            state.removeBillingInformationErrorMessage = action.payload;
        },

        // BILLING OBLIGATION
        updateBillingObligationRequest(state) {
            state.updateBillingObligationErrorMessage = undefined;
            state.updateBillingObligationLoading = true;
        },
        updateBillingObligationSuccess(state, action) {
            state.updateBillingObligationLoading = false;
            state.billingAgreement = action.payload;
        },
        updateBillingObligationFailure(state, action) {
            state.updateBillingObligationLoading = false;
            state.updateBillingObligationErrorMessage = action.payload;
        },

        cancelBillingObligationRequest(state) {
            state.cancelBillingObligationErrorMessage = undefined;
            state.cancelBillingObligationLoading = true;
        },
        cancelBillingObligationSuccess(state, action) {
            state.cancelBillingObligationLoading = false;
            state.billingAgreement = action.payload;
        },
        cancelBillingObligationFailure(state, action) {
            state.cancelBillingObligationLoading = false;
            state.cancelBillingObligationErrorMessage = action.payload;
        },

        createStripeCheckoutSessionRequest(state) {
            state.createStripeSessionLoading = true;
            state.createStripeSessionErrorMessage = null;
        },
        createStripeCheckoutSessionSuccess(state, action) {
            state.createStripeSessionLoading = false;
            state.stripeCheckoutSessionClientSecret = action.payload && action.payload.clientSecret;
        },
        createStripeCheckoutSessionFailure(state, action) {
            state.createStripeSessionLoading = false;
            state.createStripeSessionErrorMessage = action.payload;
        },

        getShopifyAppstoreTransactionsRequest(state) {
            state.appstoreTransactionsLoading = true;
            state.appstoreTransactionsErrorMessage = null;
            state.appstoreTransactionsWarningMessage = null;
            state.appstoreTransactions = null;
            state.shopifyStoreId = null;
        },
        getShopifyAppstoreTransactionsSuccess(state, action) {
            state.appstoreTransactionsLoading = false;
            state.appstoreTransactions = action.payload;
            state.shopifyStoreId = action.payload[0]?.shopifyStoreId || null;
        },
        getShopifyAppstoreTransactionsFailure(state, action) {
            state.appstoreTransactionsLoading = false;
            state.appstoreTransactionsErrorMessage = action.payload;
            state.appstoreTransactions = null;
            state.shopifyStoreId = null;
        },
        getShopifyAppstoreTransactionsWarning(state, action) {
            state.appstoreTransactionsLoading = false;
            state.appstoreTransactionsWarningMessage = action.payload;
            state.appstoreTransactions = null;
            state.shopifyStoreId = null;
        },
        toggleShowAllInvoices(state) {
            state.showAllInvoices = !state.showAllInvoices;
        },

        getStripeBillingDocumentsRequest(state) {
            state.stripeDocumentsLoading = true;
            state.stripeDocumentsErrorMessage = null;
            state.stripeDocuments = null;
        },
        getStripeBillingDocumentsSuccess(state, action) {
            state.stripeDocumentsLoading = false;
            state.stripeDocuments = action.payload;
        },
        getStripeBillingDocumentsFailure(state, action) {
            state.stripeDocumentsLoading = false;
            state.stripeDocumentsErrorMessage = action.payload;
        },

        updateInvoiceItemRequest(state, action) {
            state.updateInvoiceItemLoading[action.payload.externalUUID] = true;
            state.invoiceItemsErrorMessage = null;
        },
        updateInvoiceItemSuccess(state, action) {
            state.updateInvoiceItemLoading = null;
            if (state.invoiceItems) {
                const itemIndex = state.invoiceItems.findIndex(
                    (item) => item.externalUUID === action.payload.externalUUID
                );
                if (itemIndex >= 0) {
                    state.invoiceItems[itemIndex] = action.payload;
                }
            }
        },
        updateInvoiceItemFailure(state, action) {
            state.updateInvoiceItemLoading = null;
            state.invoiceItemsErrorMessage = action.payload;
        },

        chargeInvoiceItemRequest(state, action) {
            state.chargeInvoiceItemLoading[action.payload.externalUUID] = true;
            state.invoiceItemsErrorMessage = null;
        },
        chargeInvoiceItemSuccess(state) {
            state.chargeInvoiceItemLoading = {};
        },
        chargeInvoiceItemFailure(state, action) {
            state.chargeInvoiceItemLoading = {};
            state.invoiceItemsErrorMessage = action.payload;
        },
        getInvoiceItemsRequest(state) {
            state.getInvoiceItemsLoading = true;
            state.invoiceItems = null;
            state.invoiceItemsErrorMessage = null;
        },
        getInvoiceItemsSuccess(state, action) {
            state.getInvoiceItemsLoading = false;
            state.invoiceItems = action.payload;
        },
        getInvoiceItemsFailure(state, action) {
            state.getInvoiceItemsLoading = false;
            state.invoiceItemsErrorMessage = action.payload;
        },

        clearOrganizationErrorMessage(state) {
            state.stripeDocumentsErrorMessage = null;
            state.appstoreTransactionsErrorMessage = null;
            state.appstoreTransactionsWarningMessage = null;
            state.cancelBillingObligationErrorMessage = null;
            state.updateBillingObligationErrorMessage = null;
            state.createStripeSessionErrorMessage = null;
            state.addBillingInformationErrorMessage = null;
            state.getBillingInformationErrorMessage = null;
            state.removeBillingInformationErrorMessage = null;
            state.billingAgreementErrorMessage = null;
        },
        uploadShopifyReportRequest(state) {
            state.uploadShopifyReportLoading = true;
            state.uploadShopifyReportErrorMessage = null;
        },
        uploadShopifyReportSuccess(state) {
            state.uploadShopifyReportLoading = false;
        },
        uploadShopifyReportFailure(state, action) {
            state.uploadShopifyReportLoading = false;
            state.uploadShopifyReportErrorMessage = action.payload;
        },

        resetState() {
            return {
                ...initialReducerState,
            };
        },
    },
});

export default organizationBillingSlice;
